import { CONSUMER_CARD_DEFAULTS, EventCard } from '@eventbrite/event-renderer';
import { EventTracker, useEventClickTracker } from '@eventbrite/event-tracker';
import includes from 'lodash/includes';
import React from 'react';
import { EVENTBRITE, LINK_FOLLOW_ALLOWLIST } from '../../constants';
import {
    GA_CATEGORY_MAP,
    GA_EXTERNAL_VIEW_EVENT_ACTION,
    GA_VIEW_EVENT_ACTION,
} from '../../constants/analytics';
import { SearchEventCardProps } from '../../types/index';
import { getLocationInfo } from '../../utils/venue';
import ExpandDedupedEventCTA from './ExpandDedupedEventCTA';
import SearchEventCardDebugInfo from './SearchEventCardDebugInfo';

function createHandleEventClick(
    props: SearchEventCardProps,
    eventClickTracker: (id: string, position: number) => void,
) {
    return function _handleEventClick() {
        const {
            id,
            trackClick,
            ticketsBy,
            trackClickInternal,
            numFollowers,
            position,
        } = props;

        if (trackClick) {
            const actionType =
                ticketsBy === EVENTBRITE
                    ? GA_VIEW_EVENT_ACTION
                    : GA_EXTERNAL_VIEW_EVENT_ACTION;

            trackClick(actionType, numFollowers);
        }

        if (trackClickInternal) {
            trackClickInternal(id, ticketsBy);
        }

        if (id && position) {
            eventClickTracker(id, position);
        }
    };
}

export function SearchEventCard(props: SearchEventCardProps) {
    let {
        containerSpacingClasses,
        debugInfo,
        dedup,
        edgeColor,
        eventSalesStatus,
        experiments,
        focused,
        hideStartDate,
        history,
        id,
        imageStyle,
        imageUrl,
        isAuthenticated,
        isFree,
        isOnlineEvent,
        isSoldOut,
        minPrice,
        name,
        position,
        organizerName,
        repeatingInstanceCount,
        savedByYou,
        section,
        seriesUrl,
        shouldShowDebugOutput,
        startDate,
        startTime,
        style,
        ticketsBy,
        timezone,
        type,
        url,
        venueAddress,
        venueName,
        numFollowers,
        organizerFollowerCount,
        locationInfo,
        publicCollections,
    } = props;

    const locationString = locationInfo
        ? locationInfo
        : getLocationInfo(venueName, venueAddress);

    // Since we currently noindex children events,
    // we link their event cards to the parent event page (which is indexed)
    // so that parent events can be crawled.
    // See https://jira.evbhome.com/browse/EB-109015
    if (seriesUrl) {
        url = seriesUrl;
    }

    const shouldShowExpandDedupedEventCTA =
        dedup && typeof dedup.count === 'number' && dedup.count > 1;

    const gaCategory = section ? GA_CATEGORY_MAP[section] : undefined;
    const eventCardRef = React.useRef(null);
    const eventClickTracker = useEventClickTracker();
    const _handleEventClick = createHandleEventClick(props, eventClickTracker);

    return (
        <div className="search-event-card eds-l-mar-vert-1" ref={eventCardRef}>
            <EventTracker
                id={id}
                gaCategory={gaCategory}
                position={position}
            ></EventTracker>
            <EventCard
                {...CONSUMER_CARD_DEFAULTS}
                containerSpacingClasses={containerSpacingClasses}
                eventData={{
                    edgeColor,
                    id: id || '',
                    imageUrl: imageUrl || '',
                    isFree: isFree || false,
                    isOnlineEvent: isOnlineEvent || false,
                    isSoldOut:
                        repeatingInstanceCount && repeatingInstanceCount > 1
                            ? false
                            : isSoldOut,
                    locationInfo: locationString?.toString() || '',
                    minPrice,
                    name: name || '',
                    organizerName,
                    organizerFollowerCount:
                        numFollowers || organizerFollowerCount,
                    repeatingInstanceCount,
                    savedByYou: savedByYou || false,
                    startDate: hideStartDate ? '' : startDate,
                    startTime: startTime || '',
                    timezone: hideStartDate ? '' : timezone || '',
                    url: url || '',
                    publicCollections: publicCollections,
                }}
                onClick={_handleEventClick}
                gaCategory={gaCategory}
                isAuthenticated={isAuthenticated || false}
                isTargetBlank={true}
                eventSalesStatus={
                    repeatingInstanceCount && repeatingInstanceCount > 1
                        ? undefined
                        : eventSalesStatus
                }
                experiments={experiments}
                focused={focused}
                imageStyle={imageStyle}
                isNoFollow={!includes(LINK_FOLLOW_ALLOWLIST, ticketsBy)}
                style={style}
                isLazyImage={position !== undefined ? position > 3 : true}
                type={type}
                position={position}
            />
            {shouldShowExpandDedupedEventCTA && (
                <ExpandDedupedEventCTA
                    dedup={dedup}
                    history={history}
                    name={name}
                    eventId={id}
                />
            )}
            {debugInfo && debugInfo.esDebugInfo && shouldShowDebugOutput && (
                <SearchEventCardDebugInfo
                    debugInfo={debugInfo}
                    position={position}
                    numFollowers={numFollowers || 0}
                />
            )}
        </div>
    );
}

export default SearchEventCard;
